import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import EntityAvatar from "components/Entities/EntityAvatar";
import UserAboutIssueModal from "components/User/UserAboutIssueModal";
import UserPerspectiveForm from "components/User/UserPerspectiveForm";
import { useUser } from "util/UserContext";
import { setDefaultSiteOg, setDocumentTitle } from "util/ogMetadataUtils";
import type { IssueRanking } from "util/userUtils";
import type { QuizIssueRanking } from "types/quiz";
import QuizLanding from "./QuizLanding";
import QuizLobby from "./QuizLobby";
import QuizPart2Guesses from "./QuizPart2Guesses";
import QuizPart2Responses from "./QuizPart2Responses";
import QuizWaiting from "./QuizWaiting";
import QuizResults from "./QuizResults";
import QuizPushNotificationBanner from "./QuizPushNotificationBanner";
import { QuizProvider, useQuiz } from "./QuizProvider";
import { buildQuizShareUrl, getQuizButtonClassName, mapQuizUserToEntityAvatarUser } from "./quizUtils";

type PendingAuthAction = "create" | "join" | null;

const LoadingState: React.FC<{ message: string }> = ({ message }) => (
  <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center gap-3 px-4 py-20 text-center">
    <Loader2 className="size-6 animate-spin text-eoe-blue" aria-hidden />
    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{message}</p>
  </div>
);

const mapQuizRankingsToUserRankings = (rankings: QuizIssueRanking[] | undefined): IssueRanking[] => (
  (rankings || []).map((ranking) => ({
    id: ranking.id ?? 0,
    userId: ranking.user_id ?? 0,
    issueId: ranking.issue_id ?? 0,
    ranking: ranking.rank ?? 0,
    privacy: "public",
    createdAt: ranking.created_at ?? "",
    updatedAt: ranking.updated_at ?? "",
    issue: {
      id: ranking.issue.id ?? 0,
      name: ranking.issue.name,
      cname: ranking.issue.cname ?? "",
      properties: {},
    },
  }))
)

const QuizPageInner: React.FC = () => {
  const navigate = useNavigate();
  const { code } = useParams<{ code?: string }>();
  const normalizedCode = String(code || "").trim().toLowerCase() || null;
  const [codeDraft, setCodeDraft] = useState<string>(normalizedCode ?? "");
  const pendingAuthActionRef = useRef<PendingAuthAction>(null);
  const [latestIssueRankings, setLatestIssueRankings] = useState<IssueRanking[]>([]);
  const [latestTeabateResponse, setLatestTeabateResponse] = useState<{
    teabateId: number;
    issueId: number;
    response: number;
    importance: number;
  } | null>(null);
  const [showPart1TeabateForm, setShowPart1TeabateForm] = useState(false);
  const [isResultsCatchUpMode, setIsResultsCatchUpMode] = useState(false);

  const {
    currentUser,
    awaitingUser,
    setShowAuthModal,
    setAuthModalMode,
  } = useUser();
  const {
    ui,
    quiz,
    results,
    quizError,
    resultsError,
    isQuizLoading,
    isResultsLoading,
    qrUrl,
    createQuiz,
    joinQuiz,
    startQuiz,
    sendInvites,
    submitIssueRankings,
    submitTeabate,
    startPart2,
    submitGuesses,
    submitResponses,
    endQuiz,
    isCreating,
    isJoining,
    isStarting,
    isSendingInvites,
    isStartingPart2,
    isSavingGuesses,
    isSavingResponses,
    isEnding,
  } = useQuiz();

  const shareUrl = useMemo(
    () => (quiz?.quiz.code ? buildQuizShareUrl(quiz.quiz.code) : normalizedCode ? buildQuizShareUrl(normalizedCode) : ""),
    [normalizedCode, quiz?.quiz.code]
  );

  useEffect(() => {
    const title = normalizedCode ? `${ui.pageTitle} - ${normalizedCode.toUpperCase()}` : ui.pageTitle;
    setDocumentTitle(title);
    setDefaultSiteOg({ title, url: window.location.href });
  }, [normalizedCode, ui.pageTitle]);

  useEffect(() => {
    if (!normalizedCode) {
      setCodeDraft("");
      return;
    }
    setCodeDraft(normalizedCode);
  }, [normalizedCode]);

  useEffect(() => {
    if (!currentUser || awaitingUser) {
      return;
    }
    if (pendingAuthActionRef.current === "create" && !normalizedCode) {
      pendingAuthActionRef.current = null;
      void createQuiz()
        .then((createdQuiz) => {
          navigate(`/quiz/${createdQuiz.quiz.code}`);
        })
        .catch(() => {
          toast.error(ui.genericSaveError);
        });
    }
    if (
      pendingAuthActionRef.current === "join"
      && normalizedCode
      && quiz
      && quiz.quiz.status !== "ended"
      && !quiz.quiz.viewer_is_participant
    ) {
      pendingAuthActionRef.current = null;
      void joinQuiz().catch(() => {
        toast.error(ui.genericSaveError);
      });
    }
  }, [
    awaitingUser,
    createQuiz,
    currentUser,
    joinQuiz,
    navigate,
    normalizedCode,
    quiz,
    quiz?.quiz.viewer_is_participant,
    ui.genericSaveError,
  ]);

  useEffect(() => {
    setLatestIssueRankings(mapQuizRankingsToUserRankings(quiz?.viewer?.issue_rankings));
  }, [quiz?.viewer?.issue_rankings]);

  useEffect(() => {
    setLatestTeabateResponse(null);
  }, [quiz?.viewer?.selected_teabates]);

  useEffect(() => {
    const rankingCount = quiz?.viewer?.progress?.ranking_count ?? 0;
    const part1Complete = Boolean(quiz?.viewer?.progress?.part1_complete);
    if (
      (quiz?.quiz.status === "part1" || quiz?.quiz.status === "part2") &&
      rankingCount >= 3 &&
      !part1Complete
    ) {
      setShowPart1TeabateForm(true);
    }
  }, [
    quiz?.quiz.status,
    quiz?.viewer?.progress?.ranking_count,
    quiz?.viewer?.progress?.part1_complete,
  ]);

  useEffect(() => {
    if (quiz?.quiz.status === "ended" || quiz?.quiz.status === "lobby") {
      setShowPart1TeabateForm(false);
    }
  }, [quiz?.quiz.status]);

  useEffect(() => {
    if (quiz?.quiz.status !== "part2" || !results?.viewer?.has_pending_catch_up) {
      setIsResultsCatchUpMode(false);
    }
  }, [quiz?.quiz.status, results?.viewer?.has_pending_catch_up]);

  const wrapQuizContent = (content: React.ReactNode): React.ReactElement => (
    <>
      {currentUser && normalizedCode ? <QuizPushNotificationBanner ui={ui} /> : null}
      {content}
    </>
  )

  const requireAuthThen = (action: PendingAuthAction) => {
    pendingAuthActionRef.current = action;
    setAuthModalMode(null);
    setShowAuthModal(true);
  };

  if (!normalizedCode) {
    return (
      <QuizLanding
        ui={ui}
        codeValue={codeDraft}
        onCodeChange={setCodeDraft}
        onStart={() => {
          if (!currentUser) {
            requireAuthThen("create");
            return;
          }
          void createQuiz()
            .then((createdQuiz) => {
              navigate(`/quiz/${createdQuiz.quiz.code}`);
            })
            .catch(() => {
              toast.error(ui.genericSaveError);
            });
        }}
        onJoin={() => {
          const nextCode = codeDraft.trim().toLowerCase();
          if (!nextCode) {
            toast.error(ui.invalidCode);
            return;
          }
          navigate(`/quiz/${nextCode}`);
        }}
        isCreating={isCreating}
        isJoining={isJoining}
      />
    );
  }

  if (isQuizLoading || awaitingUser) {
    return <LoadingState message={ui.loadingQuiz} />;
  }

  if (!quiz || quizError) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-12">
        <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">{ui.pageTitle}</h1>
        <p className="text-sm text-warning-red dark:text-warning-red-dark">
          {quizError ? ui.genericLoadError : ui.invalidCode}
        </p>
      </div>
    );
  }

  if (!quiz.quiz.viewer_is_participant) {
    return wrapQuizContent(
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
            {quiz.quiz.code.toUpperCase()}
          </h1>
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.authContinueBody}</p>
        </div>
        <div className="rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
          {quiz.quiz.creator_user_id ? (
            <div className="mb-5 space-y-2 rounded-2xl border border-eoe-border-main bg-eoe-bg-hover p-4 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover">
              <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                {ui.joinHostedByLabel}
              </p>
              <EntityAvatar
                entity={mapQuizUserToEntityAvatarUser(quiz.quiz.creator)}
                entityType="user"
                entityId={quiz.quiz.creator_user_id}
                entityChip={true}
                showLink={true}
                isInputField={false}
                className="min-w-0"
              />
            </div>
          ) : null}
          {quiz.quiz.status !== "ended" ? (
            <button
              type="button"
              onClick={() => {
                if (!currentUser) {
                  requireAuthThen("join");
                  return;
                }
                void joinQuiz().catch(() => {
                  toast.error(ui.genericSaveError);
                });
              }}
              disabled={isJoining}
              className={getQuizButtonClassName({
                disabled: isJoining,
              })}
            >
              {currentUser ? ui.joinQuizAction : ui.signInToJoin}
            </button>
          ) : (
            <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.joinClosed}</p>
          )}
        </div>
      </div>
    );
  }

  const viewer = quiz.viewer;
  const progress = viewer?.progress;
  const selectedTeabateCount = viewer?.selected_teabates?.length ?? 0;
  const hasMinimumPart1Teabates = selectedTeabateCount >= 1 || latestTeabateResponse !== null;
  const canAddMorePart1Teabates = selectedTeabateCount < 5;
  const shouldShowPart1TeabateForm = Boolean(
    progress
    && (quiz.quiz.status === "part1" || quiz.quiz.status === "part2")
    && (
      !progress.part1_complete
      || (quiz.quiz.status === "part1" && showPart1TeabateForm)
    )
  );

  if (quiz.quiz.status === "lobby") {
    return wrapQuizContent(
      <QuizLobby
        ui={ui}
        quiz={quiz}
        qrUrl={qrUrl}
        shareUrl={shareUrl}
        onStart={() => {
          void startQuiz().catch(() => {
            toast.error(ui.genericSaveError);
          });
        }}
        onSendInvites={sendInvites}
        isStarting={isStarting}
        isSendingInvites={isSendingInvites}
      />
    );
  }

  if (!viewer || !progress) {
    return <LoadingState message={ui.loadingQuiz} />;
  }

  if (shouldShowPart1TeabateForm) {
    if (!progress.part1_complete && progress.ranking_count < 3) {
      return wrapQuizContent(
        <UserAboutIssueModal
          onClose={() => {
            if (latestIssueRankings.length < 3) {
              toast.error(ui.needThreeIssues)
              return
            }
            void submitIssueRankings(
              latestIssueRankings
                .slice(0, 3)
                .map((ranking) => ({
                  id: ranking.id,
                  quiz_id: null,
                  user_id: null,
                  issue_id: ranking.issueId,
                  rank: ranking.ranking,
                  sentiment: null,
                  created_at: ranking.createdAt,
                  updated_at: ranking.updatedAt,
                  issue: {
                    id: ranking.issue?.id ?? ranking.issueId,
                    name: ranking.issue?.name ?? "",
                    cname: ranking.issue?.cname ?? null,
                  },
                }))
            ).catch(() => {
              toast.error(ui.genericSaveError)
            })
          }}
          maxIssues={3}
          renderInline={true}
          title={ui.part1IssuesTitle}
          description={ui.part1IssuesDescription}
          hideAutoSubscribe={false}
          actionLabel={ui.issueModalActionLabel}
          onRankingsChange={setLatestIssueRankings}
          copy={{
            searchPlaceholder: ui.searchIssuePlaceholder,
            subscribeLabel: ui.issueModalSubscribeLabel,
            subscribeDescription: ui.issueModalSubscribeDescription,
            emptyTitle: ui.issueModalEmptyTitle,
            emptySubtitle: ui.issueModalEmptySubtitle,
            maxIssuesReached: ui.needThreeIssues,
            removeIssueLabel: ui.removeIssueAction,
          }}
        />
      )
    }

    return wrapQuizContent(
      <UserPerspectiveForm
        isOpen={true}
        renderInline={true}
        showCloseAction={true}
        requireIssueForCreation={false}
        requireAlignmentDimension={true}
        dimensionRequiredMessage={ui.dimensionRequiredMessage}
        selectedTeabates={viewer.selected_teabates || []}
        maxSelections={5}
        onClose={() => {
          if (!hasMinimumPart1Teabates) {
            toast.error(ui.needAtLeastOneTeabate)
            return
          }
          setShowPart1TeabateForm(false)
        }}
        title={ui.part1TeabateTitle}
        description={ui.part1TeabateDescription}
        onTeabateRespond={async (payload) => {
          setLatestTeabateResponse(payload)
          await submitTeabate({
            issueId: payload.issueId,
            existingPostId: payload.teabateId,
            response: payload.response,
            importance: payload.importance,
          })
        }}
        copy={{
          createButtonText: ui.perspectiveCreateButtonText,
          createButtonTooltip: ui.perspectiveCreateButtonTooltip,
          closeLabel: ui.continueToWaitingAction,
          emptyIssueTeabates: ui.noExistingTeabates,
          loadingText: ui.loadingQuiz,
          agreeLabel: ui.createAgreeLabel,
          disagreeLabel: ui.createDisagreeLabel,
          selectedTeabatesTitle: ui.selectedTeabatesTitle,
          needAtLeastOneTeabate: ui.needAtLeastOneTeabate,
          maxTeabatesReached: ui.maxTeabatesReached,
        }}
      />
    )
  }

  if (quiz.quiz.status === "part1") {
    return wrapQuizContent(
      <QuizWaiting
        ui={ui}
        quiz={quiz}
        title={quiz.quiz.is_creator ? ui.creatorReadyPart2Title : ui.waitingTitle}
        description={
          quiz.quiz.is_creator
            ? (quiz.quiz.can_start_part2 ? ui.creatorReadyPart2Description : ui.creatorReadyPart2NeedsOtherComplete)
            : ui.waitingForCreatorDescription
        }
        primaryAction={
          quiz.quiz.is_creator
            ? {
                label: ui.startPart2Action,
                onClick: () => {
                  void startPart2().catch(() => {
                    toast.error(ui.genericSaveError);
                  });
                },
                loading: isStartingPart2,
                disabled: !quiz.quiz.can_start_part2,
                helperText: !quiz.quiz.can_start_part2 ? ui.creatorReadyPart2NeedsOtherComplete : undefined,
              }
            : undefined
        }
        secondaryAction={
          canAddMorePart1Teabates
            ? {
                label: ui.addMoreTeabatesAction,
                onClick: () => setShowPart1TeabateForm(true),
              }
            : undefined
        }
        badgeMode="ready-for-part1"
      />
    );
  }

  if (quiz.quiz.status === "part2") {
    if (!progress.part2_complete && !progress.guesses_submitted) {
      return wrapQuizContent(
        <QuizPart2Guesses
          ui={ui}
          quiz={quiz}
          onSubmit={async (guesses) => {
            await submitGuesses(guesses);
          }}
          isSaving={isSavingGuesses}
        />
      );
    }

    if (!progress.part2_complete && !progress.responses_submitted) {
      return wrapQuizContent(
        <QuizPart2Responses
          ui={ui}
          quiz={quiz}
          onSubmit={async (responses) => {
            await submitResponses(responses);
          }}
          isSaving={isSavingResponses}
        />
      );
    }

    if (!progress.part2_complete) {
      return wrapQuizContent(
        <QuizWaiting
          ui={ui}
          quiz={quiz}
          title={ui.waitingTitle}
          description={ui.waitingForOthersDescription}
        />
      )
    }
  }

  if (quiz.quiz.status === "part2" || quiz.quiz.status === "ended") {
    if (isResultsLoading) {
      return <LoadingState message={ui.loadingQuiz} />;
    }

    if (!results || resultsError) {
      return wrapQuizContent(
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-12">
          <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">{ui.resultsTitle}</h1>
          <p className="text-sm text-warning-red dark:text-warning-red-dark">{ui.genericLoadError}</p>
        </div>
      )
    }

    if (
      quiz.quiz.status === "part2"
      && isResultsCatchUpMode
      && results.viewer?.has_pending_catch_up
    ) {
      if ((results.viewer.pending_target_user_ids?.length ?? 0) > 0) {
        return wrapQuizContent(
          <QuizPart2Guesses
            ui={ui}
            quiz={quiz}
            onSubmit={async (guesses) => {
              await submitGuesses(guesses);
            }}
            isSaving={isSavingGuesses}
          />
        );
      }

      if ((results.viewer.pending_quiz_teabate_ids?.length ?? 0) > 0) {
        return wrapQuizContent(
          <QuizPart2Responses
            ui={ui}
            quiz={quiz}
            onSubmit={async (responses) => {
              await submitResponses(responses);
            }}
            isSaving={isSavingResponses}
          />
        );
      }
    }

    return wrapQuizContent(
      <QuizResults
        ui={ui}
        results={results}
        onCatchUpParticipant={() => {
          setIsResultsCatchUpMode(true)
        }}
        onEndQuiz={async () => {
          try {
            await endQuiz()
          } catch {
            toast.error(ui.genericSaveError)
          }
        }}
        isEnding={isEnding}
      />
    )
  }

  return wrapQuizContent(
      <QuizWaiting
        ui={ui}
        quiz={quiz}
        title={ui.waitingTitle}
        description={ui.waitingForOthersDescription}
      />
    )
};

const QuizPage: React.FC = () => {
  const { code } = useParams<{ code?: string }>();
  return (
    <QuizProvider code={code}>
      <QuizPageInner />
    </QuizProvider>
  );
};

export default QuizPage;
